import type { NuxtError } from "nuxt/app";
import { defineStore } from "pinia";
import { type RouteLocationNormalized } from "vue-router";
import { hasOwnProperty } from "../libs/utils";

export const useDYErrorStore = defineStore('nuxt3-module-doyac-error', () => {
  const lastGlobalError = ref<Error | Partial<NuxtError>>()

  /**
   * `lastGlobalError` 가 **실제로 발생한 페이지**의 fullPath.
   *
   * 2026-08-25 [신고 URL 오기록] 전역 에러 핸들러(`DYDefault.vue` 의 `onErrorCaptured`)는
   * `router.replace('/error')` 로 에러 페이지로 보낸다. 그런데 `replace` 는 크래시한 페이지의
   * history 엔트리를 **덮어써 없애 버리므로**, 그 뒤에 사용자가 [문제 신고하기] 를 눌러도
   * 신고 화면에서는 크래시한 URL 을 되찾을 방법이 없다.
   * 그래서 관리자 푸시의 `REQUEST_URI` 가 언제나 `.../v/error`(에러 페이지 자기 자신)로 찍혔다.
   *
   * 크래시 시점에 여기에 담아두고 `DYErrorPage` 가 신고할 때 이 값을 우선 쓴다.
   * 진단용 부가정보이므로 비어 있어도 신고는 그대로 동작한다.
   */
  const lastGlobalErrorUrl = ref<string>()

  const errors = ref<Record<string, Error | Partial<NuxtError>>>({})

  const put = (error: Error | Partial<NuxtError>, url: string | RouteLocationNormalized, useThrow = true) => {
    errors.value[_makeUrlString(url)] = error
    if (useThrow) {
      throw error
    }
  }

  const get = (url: string | RouteLocationNormalized) => {
    return has(url) ? errors.value[_makeUrlString(url)] : undefined
  }

  const has = (url: string | RouteLocationNormalized) => {
    return hasOwnProperty(errors.value, _makeUrlString(url))
  }

  const remove = (url: string | RouteLocationNormalized) => {
    delete errors.value[_makeUrlString(url)]
  }

  const makeNextUrl = (url: string | RouteLocationNormalized): string => {
    const _url = _makeUrlString(url)
    console.log('_url', _url)
    const tokens = _url?.split('?')
    if (tokens.length === 1) { // 쿼리 스트링이 없던 경우
      return `${_url}?__e=1`
    } else {
      const searchParams = new URLSearchParams(tokens[1])
      if (searchParams.has('__e')) {
        const value = searchParams.get('__e')
        if (value) {
          const intVal = parseInt(value)
          if (!isNaN(intVal)) {
            searchParams.set('__e', `${intVal + 1}`)
            const nextUrl = `${tokens[0]}?${searchParams.toString()}`
            return has(nextUrl) ? makeNextUrl(nextUrl) : nextUrl
          }
        }
      }
      searchParams.set('__e', '1')
      return `${tokens[0]}?${searchParams.toString()}`
    }
  }

  const _makeUrlString = (url: string | RouteLocationNormalized) => {
    return typeof url === 'string' ? url : url.fullPath
  }

  return {
    lastGlobalError,
    lastGlobalErrorUrl,
    errors,
    put,
    get,
    has,
    remove,
    makeNextUrl
  }
})